import React from 'react';
import {Divider, Skeleton, Stack} from '@mui/material';
import Box from '@mui/material/Box';
import {Card, Spacer} from 'components';

// Mirrors WorkPreviewContent while work and notes load.
const RowSkeleton: React.FC<{labelWidth?: number; valueWidth?: number}> = ({
  labelWidth = 90,
  valueWidth = 120,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '25% 1fr',
      columnGap: 2,
      alignItems: 'center',
    }}>
    <Skeleton variant="text" width={labelWidth} sx={{fontSize: '0.75rem'}} />
    <Skeleton variant="text" width={valueWidth} />
  </Box>
);

const GroupSkeleton: React.FC<{rows: [number, number][]; heading?: number}> = ({
  rows,
  heading,
}) => (
  <Stack spacing={0.5}>
    {heading ? (
      <Skeleton variant="text" width={heading} sx={{fontSize: '0.75rem'}} />
    ) : null}
    {rows.map(([labelWidth, valueWidth], i) => (
      <RowSkeleton key={i} labelWidth={labelWidth} valueWidth={valueWidth} />
    ))}
  </Stack>
);

const ManifestationRowSkeleton: React.FC = () => (
  <Card sx={{p: 1}}>
    <Stack direction="row" spacing={1.5} sx={{alignItems: 'center'}}>
      <Skeleton variant="circular" width={20} height={20} />
      <Skeleton variant="rounded" width={16} height={16} />
      <Stack spacing={0.25} sx={{flex: 1}}>
        <Skeleton variant="text" width={220} sx={{fontSize: '0.75rem'}} />
        <Skeleton variant="text" width={60} sx={{fontSize: '0.75rem'}} />
      </Stack>
      <Skeleton variant="rounded" width={56} height={22} />
    </Stack>
  </Card>
);

const ExpressionSkeleton: React.FC<{manifestations: number}> = ({
  manifestations,
}) => (
  <Card level="level2">
    <Stack spacing={1}>
      <Skeleton variant="text" width={150} sx={{fontSize: '0.75rem'}} />
      <Skeleton variant="text" width={110} sx={{fontSize: '0.75rem'}} />
      <Spacer size={1} />
      {Array.from({length: manifestations}).map((_, i) => (
        <ManifestationRowSkeleton key={i} />
      ))}
    </Stack>
  </Card>
);

const HeaderSkeleton: React.FC = () => (
  <Stack
    direction="row"
    sx={{
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 40,
      pl: 2,
      pr: 1,
      mb: 1,
    }}>
    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
      <Skeleton variant="text" width={200} sx={{fontSize: '1.5rem'}} />
      <Skeleton variant="circular" width={20} height={20} />
    </Stack>
    <Skeleton variant="rounded" width={110} height={32} />
  </Stack>
);

type Props = {
  /** Include the title row, for when EntityPreview has no header yet. */
  withHeader?: boolean;
};

export const PreviewSkeleton: React.FC<Props> = ({withHeader}) => (
  <Stack>
    {withHeader ? <HeaderSkeleton /> : null}
    <Stack divider={<Divider sx={{my: 1}} />}>
      <GroupSkeleton
        rows={[
          [40, 40],
          [90, 110],
          [130, 30],
        ]}
      />
      <GroupSkeleton
        rows={[
          [120, 160],
          [50, 100],
        ]}
      />
      <GroupSkeleton
        heading={70}
        rows={[
          [80, 90],
          [100, 70],
        ]}
      />
      <GroupSkeleton
        rows={[
          [100, 90],
          [120, 60],
        ]}
      />
      <GroupSkeleton heading={40} rows={[[110, 150]]} />
    </Stack>
    <Spacer height={2} />
    <Stack spacing={2}>
      <ExpressionSkeleton manifestations={1} />
      <ExpressionSkeleton manifestations={1} />
    </Stack>
  </Stack>
);
